import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { deleteUser } from 'firebase/auth';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import styles from './Configuraciones.module.css';
import { ChevronDown, ChevronUp, Trash2, Bell, KeyRound } from 'lucide-react';

export default function Configuraciones() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [openPanel, setOpenPanel] = useState(null);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('');
  const [showDeletePanel, setShowDeletePanel] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setUser(auth.currentUser);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(''), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
  };

  const togglePanel = (panel) => {
    setOpenPanel(openPanel === panel ? null : panel);
  };

  const handleToggleNotifications = () => setNotificationsEnabled((prev) => !prev);

  const handleDeleteAccount = async () => {
    setSaving(true);
    try {
      await deleteUser(user);
      showToast('✅ Cuenta eliminada exitosamente. Hasta pronto 👋', 'success');
      setTimeout(() => navigate('/'), 2500);
    } catch (error) {
      console.error(error);
      showToast('❌ Error al eliminar la cuenta. Intenta iniciar sesión de nuevo.', 'error');
    } finally {
      setSaving(false);
      setShowDeletePanel(false);
    }
  };

  if (loading) return <p>Cargando configuración...</p>;

  const hideMenu = location.pathname.startsWith('/configuraciones/cambiar-contraseña');

  if (hideMenu) {
    return (
      <div className={styles.configContainer}>
        <h2>Configuraciones de Duxbyte</h2>
        <Outlet />
      </div>
    );
  }

  const renderToggle = (label, icon, panelKey) => (
    <div
      className={styles.toggleHeader}
      onClick={() => togglePanel(panelKey)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') togglePanel(panelKey);
      }}
    >
      <div className={styles.labelWithIcon}>
        {icon}
        <span>{label}</span>
      </div>
      {openPanel === panelKey ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
    </div>
  );

  return (
    <div className={styles.configContainer}>
      <h2>Configuraciones de Duxbyte</h2>

      {toastMessage && (
        <div className={`${styles.toast} ${toastType === 'success' ? styles.success : styles.error}`}>
          {toastMessage}
        </div>
      )}

      <Outlet />

      <section className={styles.configSection}>
        {renderToggle('Quiero cambiar la contraseña', <KeyRound size={18} />, 'password')}
        {openPanel === 'password' && (
          <div className={styles.panelContent}>
            <button
              className={styles.changePasswordBtn}
              onClick={() => navigate('/configuraciones/cambiar-contraseña')}
            >
              Ir a cambiar contraseña
            </button>
          </div>
        )}

        {renderToggle('¿Notificaciones?', <Bell size={18} />, 'notifications')}
        {openPanel === 'notifications' && (
          <div className={styles.panelContent}>
            <label className={styles.switchLabel}>
              Recibir notificaciones de Duxbyte
              <input
                type="checkbox"
                checked={notificationsEnabled}
                onChange={handleToggleNotifications}
                disabled={saving}
              />
            </label>
          </div>
        )}

        {renderToggle('Eliminar cuenta', <Trash2 size={18} />, 'delete')}
        {openPanel === 'delete' && (
          <div className={styles.panelContent}>
            <button
              className={styles.deleteAccountBtn}
              onClick={() => setShowDeletePanel(true)}
              disabled={saving}
            >
              Eliminar mi cuenta
            </button>
          </div>
        )}
      </section>

      {/* PANEL DE CONFIRMACIÓN */}
      {showDeletePanel && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3>¿Estás seguro?</h3>
            <p>Esta acción eliminará tu cuenta de forma permanente. ⚠️</p>
            <div className={styles.modalActions}>
              <button
                className={styles.confirmDelete}
                onClick={handleDeleteAccount}
                disabled={saving}
              >
                Sí, eliminar cuenta
              </button>
              <button
                className={styles.cancelDelete}
                onClick={() => setShowDeletePanel(false)}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
